import type { ReactNode } from 'react';
import type { Bulle, Etat } from '../partage/api/types';
import { piecesJointes, pastillesDeLaFace, quandEnMots, type PastilleDeLaFace } from './ficheEnMots';
import { IconeDePiece } from './PiecesDeLaFiche';
import { Plus } from './ReglagesDeLaFiche';
import type { Intercalaire } from './PanneauDesReglages';
import {
  ADRESSE,
  CONDITION,
  CONDITIONS,
  CONTACT,
  difficulteExpliquee,
  LIEN_WEB,
  MOTS_DES_INTERCALAIRES,
  MOTS_ETAT,
  ouvrirLIntercalaire,
  QUAND,
} from '../partage/vocabulaire';
import './FaceDeLaFiche.css';

/*
 * La face de la fiche de l'ordinateur (chantier reglages-de-la-fiche, modification 03 ; maquette
 * du carnet `fiche-quatre-types.html`, la fiche Classique : `.etats`, `.pastilles`, `.rubrique`,
 * `.champ`, `.lien`) : **la fiche montre, les Réglages changent.** Rien ne s'y règle ; un clic sur
 * une pastille, sur une ligne de Quand ou sur le contact ouvre les Réglages sur l'intercalaire qui
 * le règle. Les mots sont ceux des Réglages (`pastillesDeLaFace`, `quandEnMots`), jamais recalculés.
 */

/** Les quatre états, dans l'ordre de la maquette ; celui de la bulle allumé. Ils se lisent. */
const ETATS: readonly Etat[] = ['a_faire', 'en_attente_confirmation', 'fait', 'sans_objet'];

export function EtatsDeLaFace({ etat }: { etat: Etat }) {
  return (
    <div className="face-etats">
      {ETATS.map((e) => (
        <span key={e} className={e === etat ? 'est-ici' : undefined} aria-current={e === etat ? 'true' : undefined}>
          {MOTS_ETAT[e]}
        </span>
      ))}
    </div>
  );
}

/** Ce qui est réglé, en pastilles ; chacune ouvre l'intercalaire État. Rien de réglé : rien. */
export function PastillesDeLaFace({ bulle, onOuvrir }: { bulle: Bulle; onOuvrir: (i: Intercalaire) => void }) {
  const pastilles = pastillesDeLaFace(bulle);
  if (pastilles.length === 0) return null;
  const titre = ouvrirLIntercalaire(MOTS_DES_INTERCALAIRES.etat);
  return (
    <div className="face-pastilles">
      {pastilles.map((p) => (
        <button
          key={p.cle}
          type="button"
          className={`face-pastille face-pastille--${p.cle}`}
          title={p.cle === 'difficulte' ? difficulteExpliquee(bulle.charge) : titre}
          onClick={() => onOuvrir('etat')}
        >
          <IconeDeLaPastille cle={p.cle} />
          {p.mot}
        </button>
      ))}
    </div>
  );
}

/** L'horloge de la durée, comme la maquette (« ⏱ 1 h 25 ») ; les autres pastilles n'en ont pas. */
function IconeDeLaPastille({ cle }: { cle: PastilleDeLaFace['cle'] }) {
  if (cle !== 'duree') return null;
  return (
    <span className="face-pastille-icone" aria-hidden="true">
      ⏱
    </span>
  );
}

/** Une rubrique de la face : son intertitre en petites capitales, au-dessus d'un filet pointillé. */
export function RubriqueDeLaFace({ titre, children }: { titre: string; children: ReactNode }) {
  return (
    <section className="face-rubrique">
      <h4 className="face-rubrique-titre">{titre}</h4>
      {children}
    </section>
  );
}

/** « Quand » : ce qui est posé du temps, et la date au plus tôt ; un clic ouvre l'intercalaire Dates. */
export function QuandDeLaFace({ bulle, onOuvrir }: { bulle: Bulle; onOuvrir: (i: Intercalaire) => void }) {
  const lignes = quandEnMots(bulle);
  if (lignes.length === 0) return null;
  const titre = ouvrirLIntercalaire(MOTS_DES_INTERCALAIRES.dates);
  return (
    <RubriqueDeLaFace titre={QUAND}>
      {lignes.map((l) => (
        <button key={l.cle} type="button" className="face-champ" title={titre} onClick={() => onOuvrir('dates')}>
          <span>{l.nom}</span>
          <b>{l.valeur}</b>
        </button>
      ))}
    </RubriqueDeLaFace>
  );
}

/**
 * « Conditions », sur la fiche Plan seule (chantier liens-entre-bulles, ticket 05, décision 13 du
 * cadrage) : ce qu'elle attend et ce qu'elle retient (le bloc des conditions, `children`), ce n'est
 * pas un réglage ; et « + Condition », toujours là, qui en pose une.
 */
export function ConditionsDeLaFace({ onPoserUneCondition, children }: { onPoserUneCondition: () => void; children: ReactNode }) {
  return (
    <RubriqueDeLaFace titre={CONDITIONS}>
      {children}
      <button type="button" className="piece-ajouter face-condition" data-tuto="ajouter-condition" onClick={onPoserUneCondition}>
        <Plus />
        {CONDITION}
      </button>
    </RubriqueDeLaFace>
  );
}

/**
 * Le contact et le lieu en une ligne, s'ils sont posés : un clic ouvre l'intercalaire Contact. Le
 * lien, s'il est posé, s'ouvre à côté dans un autre onglet. Rien de posé : rien.
 */
export function ContactDeLaFace({ bulle, onOuvrir }: { bulle: Bulle; onOuvrir: (i: Intercalaire) => void }) {
  const pieces = piecesJointes(bulle);
  const contactEtLieu = pieces.filter((p) => p.sorte !== 'lien');
  const lien = pieces.find((p) => p.sorte === 'lien');
  if (pieces.length === 0) return null;
  return (
    <div className="face-contact">
      {contactEtLieu.length > 0 && (
        <button
          type="button"
          className="face-contact-ligne"
          title={ouvrirLIntercalaire(MOTS_DES_INTERCALAIRES.contact)}
          aria-label={contactEtLieu.map((p) => `${p.sorte === 'adresse' ? ADRESSE : CONTACT} : ${p.nom}`).join(' · ')}
          onClick={() => onOuvrir('contact')}
        >
          {contactEtLieu.map((p) => (
            <span key={p.sorte} className="face-contact-piece">
              <IconeDePiece sorte={p.sorte} />
              {p.nom}
            </span>
          ))}
        </button>
      )}
      {lien && bulle.lien && (
        <a className="face-contact-lien" href={bulle.lien} target="_blank" rel="noopener noreferrer" aria-label={`${LIEN_WEB} : ${lien.nom}`}>
          <IconeDePiece sorte="lien" />
          {lien.nom}
        </a>
      )}
    </div>
  );
}
